import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatUSD } from "@/lib/api";
import { X } from "lucide-react";

export const FULFILL_STEPS = ["unfulfilled", "packed", "shipped", "delivered"];
export const FULFILL_LABEL = { unfulfilled: "Needs Packing", packed: "Packed", shipped: "Shipped", delivered: "Delivered" };
export const FULFILL_COLOR = {
  unfulfilled: "bg-white/10 text-white/70",
  packed: "bg-amber-900 text-amber-100",
  shipped: "bg-blue-900 text-blue-100",
  delivered: "bg-emerald-900 text-emerald-100",
};

export function FulfillmentModal({ order, onClose, onSaved }) {
  const [status, setStatus] = useState(order.fulfillment_status || "unfulfilled");
  const [tracking, setTracking] = useState(order.tracking_number || "");
  const [carrier, setCarrier] = useState(order.carrier || "");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);
  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/admin/orders/${order.session_id}/fulfillment`, {
        status, tracking_number: tracking, carrier, notes,
      });
      toast.success(`Marked as ${FULFILL_LABEL[status]}`);
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-6" onClick={onClose}>
      <div className="surface p-6 max-w-lg w-full" onClick={e => e.stopPropagation()} data-testid="fulfillment-modal">
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="font-serif text-2xl">Update Fulfillment</div>
            <div className="overline text-white/40 mt-1">{order.artwork_title}</div>
          </div>
          <button onClick={onClose} className="text-white/50 hover:text-white"><X size={18}/></button>
        </div>
        <div className="mb-6">
          <div className="overline text-white/50 mb-3">Status</div>
          <div className="grid grid-cols-4 gap-1">
            {FULFILL_STEPS.map((s, i) => {
              const currentIdx = FULFILL_STEPS.indexOf(status);
              const active = i <= currentIdx;
              return (
                <button key={s} onClick={() => setStatus(s)} data-testid={`fulfill-step-${s}`}
                  className={`px-2 py-2 text-[10px] uppercase tracking-widest border ${active ? "bg-white text-black border-white" : "bg-transparent text-white/60 border-white/20"}`}>
                  {i + 1}. {FULFILL_LABEL[s]}
                </button>
              );
            })}
          </div>
        </div>
        <div className="space-y-3">
          <div>
            <div className="overline text-white/50 mb-2">Carrier</div>
            <input className="w-full bg-black/40 border border-white/10 px-3 py-2 text-sm" placeholder="USPS, FedEx, UPS…" value={carrier} onChange={e => setCarrier(e.target.value)} data-testid="fulfill-carrier"/>
          </div>
          <div>
            <div className="overline text-white/50 mb-2">Tracking Number</div>
            <input className="w-full bg-black/40 border border-white/10 px-3 py-2 text-sm" value={tracking} onChange={e => setTracking(e.target.value)} data-testid="fulfill-tracking"/>
          </div>
          <div>
            <div className="overline text-white/50 mb-2">Note (optional)</div>
            <textarea rows={2} className="w-full bg-black/40 border border-white/10 px-3 py-2 text-sm" value={notes} onChange={e => setNotes(e.target.value)} data-testid="fulfill-notes"/>
          </div>
        </div>
        {order.fulfillment_history?.length > 0 && (
          <div className="mt-6 border-t border-white/10 pt-4">
            <div className="overline text-white/50 mb-3">History</div>
            <div className="space-y-1 text-xs font-mono-brand text-white/60 max-h-32 overflow-y-auto">
              {order.fulfillment_history.map((h, i) => (
                <div key={`${h.status}-${h.at || i}`}>
                  <span className="text-white">{FULFILL_LABEL[h.status] || h.status}</span>
                  <span className="text-white/40"> · {h.at?.slice(0, 16).replace("T", " ")}</span>
                  {h.notes && <span className="text-white/50"> — {h.notes}</span>}
                </div>
              ))}
            </div>
          </div>
        )}
        <div className="mt-6 flex gap-3">
          <button onClick={save} disabled={saving} data-testid="fulfill-save" className="bg-white text-black px-6 py-2 text-xs uppercase tracking-widest disabled:opacity-50">
            {saving ? "Saving…" : "Save"}
          </button>
          <button onClick={onClose} className="border border-white/20 px-6 py-2 text-xs uppercase tracking-widest text-white/70">Cancel</button>
        </div>
      </div>
    </div>
  );
}

export function Sales() {
  const [orders, setOrders] = useState([]);
  const [modalOrder, setModalOrder] = useState(null);
  const [filter, setFilter] = useState("all");
  const [csvStart, setCsvStart] = useState("");
  const [csvEnd, setCsvEnd] = useState("");
  const load = () => api.get("/admin/orders").then(r => setOrders(r.data));
  useEffect(() => { load(); }, []);

  const setPreset = (days) => {
    const end = new Date();
    const start = new Date();
    start.setDate(end.getDate() - days);
    const iso = (d) => d.toISOString().slice(0, 10);
    setCsvStart(iso(start));
    setCsvEnd(iso(end));
  };

  const downloadCsv = async () => {
    if (csvStart && csvEnd && csvStart > csvEnd) {
      toast.error("Start date must be on or before End date");
      return;
    }
    const params = new URLSearchParams();
    if (csvStart) params.set("start", csvStart);
    if (csvEnd) params.set("end", csvEnd);
    try {
      const res = await api.get(`/admin/sales/export?${params.toString()}`, { responseType: "blob" });
      const blob = new Blob([res.data], { type: "text/csv" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `sales_${csvStart || "all"}_to_${csvEnd || "now"}.csv`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success("Sales CSV downloaded");
    } catch (e) {
      // Blob-responseType errors need JSON parsing from the blob
      let msg = "Export failed";
      try {
        const txt = await e?.response?.data?.text?.();
        if (txt) msg = JSON.parse(txt).detail || msg;
      } catch (parseErr) { console.error("Could not parse export error", parseErr); }
      toast.error(msg);
    }
  };

  const paid = orders.filter(o => o.payment_status === "paid");
  const counts = {
    all: orders.length,
    unfulfilled: paid.filter(o => (o.fulfillment_status || "unfulfilled") === "unfulfilled").length,
    packed: paid.filter(o => o.fulfillment_status === "packed").length,
    shipped: paid.filter(o => o.fulfillment_status === "shipped").length,
    delivered: paid.filter(o => o.fulfillment_status === "delivered").length,
  };
  const visible = filter === "all" ? orders : paid.filter(o => (o.fulfillment_status || "unfulfilled") === filter);

  return (
    <div>
      <h1 className="font-serif text-4xl mb-1">Sales & Fulfillment</h1>
      <div className="overline text-white/40 mb-6">Track each order through Paid → Packed → Shipped → Delivered</div>

      {/* Tax / Sales CSV export */}
      <div className="surface p-4 mb-6" data-testid="tax-csv-export">
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <div className="overline text-white/40 mb-1">Start</div>
            <input type="date" value={csvStart} onChange={e => setCsvStart(e.target.value)} data-testid="csv-start" className="bg-transparent border border-white/15 px-3 py-2 text-sm"/>
          </div>
          <div>
            <div className="overline text-white/40 mb-1">End</div>
            <input type="date" value={csvEnd} onChange={e => setCsvEnd(e.target.value)} data-testid="csv-end" className="bg-transparent border border-white/15 px-3 py-2 text-sm"/>
          </div>
          {[["7d", 7], ["30d", 30], ["90d", 90], ["365d", 365]].map(([label, days]) => (
            <button key={label} onClick={() => setPreset(days)} data-testid={`csv-preset-${label}`}
              className="px-3 py-2 text-[10px] uppercase tracking-widest border border-white/15 text-white/70 hover:border-white/40">
              Last {label}
            </button>
          ))}
          <button onClick={() => { setCsvStart(""); setCsvEnd(""); }} data-testid="csv-preset-all"
            className="px-3 py-2 text-[10px] uppercase tracking-widest border border-white/15 text-white/70 hover:border-white/40">
            All Time
          </button>
          <button onClick={downloadCsv} data-testid="download-csv-btn"
            className="ml-auto px-4 py-2 text-xs uppercase tracking-widest bg-white text-black">
            Download Tax CSV
          </button>
        </div>
      </div>

      <div className="flex flex-wrap gap-2 mb-6">
        {[
          ["all", "All"],
          ["unfulfilled", "Needs packing"],
          ["packed", "Packed"],
          ["shipped", "Shipped"],
          ["delivered", "Delivered"],
        ].map(([k, label]) => (
          <button key={k} onClick={() => setFilter(k)} data-testid={`sales-filter-${k}`}
            className={`px-3 py-2 text-[10px] uppercase tracking-widest border ${filter === k ? "bg-white text-black border-white" : "border-white/15 text-white/70"}`}>
            {label} · {counts[k]}
          </button>
        ))}
      </div>

      <div className="surface overflow-x-auto">
        <table className="w-full text-sm font-mono-brand">
          <thead className="text-white/50 text-xs uppercase">
            <tr>
              <th className="text-left p-3">Date</th>
              <th className="text-left p-3">Buyer</th>
              <th className="text-left p-3">Item(s)</th>
              <th className="text-right p-3">Amount</th>
              <th className="text-left p-3">Payment</th>
              <th className="text-left p-3">Fulfillment</th>
              <th className="text-left p-3">Gelato</th>
              <th className="text-right p-3">Action</th>
            </tr>
          </thead>
          <tbody>
            {visible.map(o => {
              const fstatus = o.fulfillment_status || (o.payment_status === "paid" ? "unfulfilled" : "");
              return (
                <tr key={o.session_id} className="border-t border-white/5" data-testid={`order-${o.session_id}`}>
                  <td className="p-3 text-white/60">{o.created_at?.slice(0, 10)}</td>
                  <td className="p-3">{o.buyer_name}<div className="text-xs text-white/40">{o.buyer_email}</div></td>
                  <td className="p-3">
                    <div className="text-white/90 truncate max-w-[280px]" title={o.artwork_title}>{o.artwork_title}</div>
                    <div className="text-xs text-white/40">{o.size}</div>
                  </td>
                  <td className="p-3 text-right">{formatUSD(o.amount)}</td>
                  <td className="p-3">
                    <span className={`px-2 py-0.5 text-[10px] uppercase ${o.payment_status === "paid" ? "bg-emerald-900 text-emerald-200" : "bg-white/10 text-white/60"}`}>
                      {o.payment_status}
                    </span>
                    {o.payment_method && <div className="text-[10px] text-white/50 mt-1" data-testid={`payment-method-${o.session_id}`}>{o.payment_method}</div>}
                  </td>
                  <td className="p-3">
                    {o.payment_status === "paid" ? (
                      <span className={`px-2 py-0.5 text-[10px] uppercase ${FULFILL_COLOR[fstatus] || "bg-white/10"}`} data-testid={`fulfill-badge-${o.session_id}`}>
                        {FULFILL_LABEL[fstatus] || fstatus}
                      </span>
                    ) : <span className="text-white/30 text-xs">—</span>}
                    {o.tracking_number && <div className="text-[10px] text-white/40 mt-1 font-mono-brand">{o.carrier || ""} {o.tracking_number}</div>}
                  </td>
                  <td className="p-3">
                    {o.all_prints ? (
                      <div>
                        <span className={`px-2 py-0.5 text-[10px] uppercase ${
                          o.gelato_status === "shipped" ? "bg-emerald-900 text-emerald-200" :
                          o.gelato_status === "created" ? "bg-blue-900 text-blue-200" :
                          o.gelato_status === "failed" ? "bg-red-900 text-red-200" :
                          "bg-white/10 text-white/60"
                        }`} data-testid={`gelato-badge-${o.session_id}`}>
                          {o.gelato_status || "—"}
                        </span>
                        {o.gelato_order_id && <div className="text-[10px] text-white/40 mt-1 font-mono-brand">{o.gelato_order_id}</div>}
                      </div>
                    ) : <span className="text-white/30 text-xs">Original</span>}
                  </td>
                  <td className="p-3 text-right">
                    <div className="flex gap-1 justify-end">
                      {o.payment_status === "paid" && (
                        <button onClick={() => setModalOrder(o)} data-testid={`update-fulfillment-${o.session_id}`}
                          className="px-3 py-1 text-[10px] uppercase tracking-widest bg-white/10 hover:bg-white/20">
                          Update
                        </button>
                      )}
                      {o.all_prints && o.payment_status === "paid" && (o.gelato_status === "failed" || o.gelato_status === "pending") && (
                        <button
                          onClick={async () => {
                            try {
                              const { data } = await api.post(`/admin/orders/${o.session_id}/resubmit-gelato`);
                              if (data.ok) toast.success(`Sent to Gelato · ${data.gelato_order_id}`);
                              else toast.error("Gelato retry failed");
                              load();
                            } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
                          }}
                          data-testid={`resubmit-gelato-${o.session_id}`}
                          className="px-3 py-1 text-[10px] uppercase tracking-widest bg-amber-900 text-amber-100 hover:bg-amber-800">
                          Retry Gelato
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
            {visible.length === 0 && <tr><td colSpan={8} className="p-8 text-center text-white/40">No orders in this view.</td></tr>}
          </tbody>
        </table>
      </div>

      {modalOrder && <FulfillmentModal order={modalOrder} onClose={() => setModalOrder(null)} onSaved={() => { setModalOrder(null); load(); }} />}
    </div>
  );
}

